import React, { useEffect, useState } from 'react';
import { ILegalTransactionRelationship } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef, GridToolbar, deDE } from '@mui/x-data-grid';
import { GrundbuchblattEditDialog } from '../../Grundstuecke/GrundbuchblattEditDialog';
import { ILegalTransactionFullObject } from '../../Interfaces/ILegalTransaction';
import { IDocumentDirectoryHasDocument } from '../../Interfaces/IDocumentDirectoryHasDocument';
import { IDocumentDirectory } from '../../Interfaces/IDocumentDirectory';
import { ILegalPhaseFullObject } from '../../Interfaces/ILegalPhase';
import { ILegalTransactionRelationshipRealEstate } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/ILegalTransactionRelationshipRealEstate';
import { Add, Delete, MenuBook } from '@mui/icons-material';
import { LegalTransactionRelationshipTypeEnum } from '../../Interfaces/ILegalTransactionRelationship/LegalTransactionRelationshipTypeEnum';
import {
  useAdministrativeUnits,
  useCourtAdministrativeUnitAssignments,
  useCourts,
} from '../../Aemter/hooks';
import { ILegalRequirementState } from '../../Interfaces/ILegalRequirementState';
import { AssignRealEstateDialog } from './AssignRealEstateDialog';
import { ILegalTransactionGrundbuchblattLink } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';

interface IProps {
  legalTransaction: ILegalTransactionFullObject;
  //
  relationsships: ILegalTransactionRelationship[];
  setRelationships: Function;
  // Documente
  documentArray: IDocumentDirectoryHasDocument[];
  documentDirectoryArray: IDocumentDirectory[];
  setDocumentArray: Function;
  setDocumentDirectoryArray: Function;
  // Phase
  legalPhaseFullObjectArray: ILegalPhaseFullObject[];
  setLegalPhaseFullObjectArray: Function;
  legalReqStates: ILegalRequirementState[];
  /**
   * Externer Trigger zum Oeffnen des Add-Dialogs. Wird vom Parent
   * (ProcessManagerTab) inkrementiert, wenn der User auf der Karte den
   * gemeinsamen +-Button -> "Grundstueck" auswaehlt.
   */
  addTriggerToken?: number;
  /** Plus-Button INNERHALB der Tabelle anzeigen (default true). Wenn der
   * Add-Trigger ueber den Karten-Header erfolgt, kann er ausgeblendet
   * werden. */
  showInlineAddButton?: boolean;
}

export const LegalTransactionRealEstates: React.FC<IProps> = ({
  legalTransaction,
  relationsships,
  setRelationships,
  addTriggerToken,
  showInlineAddButton = true,
}) => {
  // 1=Kaufvertrag, 3=Hypothek, 9=Grundbuchauszug, 8=Unterschriftsbeglaubigung,
  // 12=Allgemeine Anfrage (Dialog-Eingang) — alle können Immobilien tragen.
  const relationWithRealEstate = relationsships.filter(relationship =>
    [1, 3, 9, 8, 12].includes(relationship.idRelationshipType)
  );
  //
  const [isOpenAdd, setIsOpenAdd] = useState(false);
  // #1166: welches Grundbuchblatt gerade im Editor offen ist (ein Dialog für
  // das ganze Grid, statt pro Zeile).
  const [openBlattId, setOpenBlattId] = useState<number | null>(null);

  const onAdd = () => {
    setIsOpenAdd(true);
  };

  // Externer Trigger (Karten-Header-Plus-Button) — jedes Mal, wenn der
  // Parent das Token aendert, oeffnen wir den Add-Dialog. ``onAdd`` ueber
  // ein Ref aufrufen, damit wir es bewusst NICHT in die Dep-Liste packen
  // muessen (onAdd wechselt bei jedem Render die Referenz, wuerde sonst
  // bei jedem State-Update neu feuern).
  const lastTriggerRef = React.useRef<number | undefined>(addTriggerToken);
  const onAddRef = React.useRef(onAdd);
  onAddRef.current = onAdd;
  useEffect(() => {
    if (
      addTriggerToken !== undefined &&
      addTriggerToken !== lastTriggerRef.current
    ) {
      lastTriggerRef.current = addTriggerToken;
      onAddRef.current();
    }
  }, [addTriggerToken]);

  const handleClose = () => {
    setIsOpenAdd(false);
  };

  /** Übernimmt das im AssignRealEstateDialog gewählte/erfasste Grundstück als
   *  neue Grundbuchauszug-Beziehung des Vorgangs. */
  const handleConfirmRealEstate = (
    realEstate: ILegalTransactionRelationshipRealEstate
  ) => {
    const newRelationsship: ILegalTransactionRelationship = {
      Created: new Date(),
      DocumentArray: [],
      idLegalTransaction: -1,
      idRelationship: -1,
      idRelationshipType:
        LegalTransactionRelationshipTypeEnum.LandRegisterQuery,
      isActiveRelationship: true,
      isCompanyFormation: false,
      Members: [],
      PartnerIdentifiers: [],
      RealEstateArray: [realEstate],
      RelationshipType: 'Grundbuchauszug',
      syncWithArnoTop: false,
      Updated: new Date(),
      data: { ReasonLandRegisterQueryArray: [] },
    };

    setRelationships([...relationsships, newRelationsship]);
    setIsOpenAdd(false);
  };

  // Blatt-zentrische Zuordnungen (Relationship_has_Grundbuchblatt). Hinzufügen UND
  // Entfernen werden erst beim Speichern des Vorgangs via syncRelationshipBlaetter
  // persistiert (keine sofortige serverseitige Verknüpfung).
  const updateBlattArray = (
    relationship: ILegalTransactionRelationship,
    updatedBlattArray: ILegalTransactionGrundbuchblattLink[]
  ) => {
    setRelationships([
      ...relationsships.map(relation =>
        relation.idRelationship !== relationship.idRelationship
          ? relation
          : {
              ...relation,
              GrundbuchblattArray: updatedBlattArray,
            }
      ),
    ]);
  };

  /** Aus der Recherche angelegte, zuzuordnende Blätter in die Grundbuch-Beziehung
   *  des Vorgangs aufnehmen (bestehende LandRegisterQuery-Beziehung oder neue). Die
   *  Verknüpfung selbst entsteht erst beim Speichern des Vorgangs. */
  const handleAssignBlaetter = (
    links: ILegalTransactionGrundbuchblattLink[]
  ) => {
    if (!links || links.length === 0) return;
    const target = relationsships.find(
      r =>
        r.idRelationshipType ===
        LegalTransactionRelationshipTypeEnum.LandRegisterQuery
    );
    if (target) {
      const existing = target.GrundbuchblattArray ?? [];
      const have = new Set(existing.map(b => b.idGrundbuchblatt));
      updateBlattArray(target, [
        ...existing,
        ...links.filter(l => !have.has(l.idGrundbuchblatt)),
      ]);
    } else {
      const newRelationship: ILegalTransactionRelationship = {
        Created: new Date(),
        DocumentArray: [],
        idLegalTransaction: -1,
        idRelationship: -1,
        idRelationshipType:
          LegalTransactionRelationshipTypeEnum.LandRegisterQuery,
        isActiveRelationship: true,
        isCompanyFormation: false,
        Members: [],
        PartnerIdentifiers: [],
        RealEstateArray: [],
        GrundbuchblattArray: links,
        RelationshipType: 'Grundbuchauszug',
        syncWithArnoTop: false,
        Updated: new Date(),
        data: { ReasonLandRegisterQueryArray: [] },
      };
      setRelationships([...relationsships, newRelationship]);
    }
  };

  const { courts, isLoadingCourts } = useCourts();
  const { administrativeUnits, isLoadingAdministrativeUnits } =
    useAdministrativeUnits();
  const {
    courtAdministrativeUnitAssignments,
    isLoadingCourtAdministrativeUnitAssignments,
  } = useCourtAdministrativeUnitAssignments();

  if (
    isLoadingCourts ||
    isLoadingAdministrativeUnits ||
    isLoadingCourtAdministrativeUnitAssignments
  ) {
    return <CircularProgress />;
  }

  if (!courts || !administrativeUnits || !courtAdministrativeUnitAssignments) {
    return (
      <Alert severity='error'>
        Es ist ein Fehler aufgetretten!
        <br />
        Die Gerichte konnten nicht bezogen werden.
      </Alert>
    );
  }

  // #1166: Grid-Zeilen rein aus den verknüpften Grundbuchblättern
  // (RealEstateArray wird nicht mehr angezeigt — blatt-zentrisch).
  const blattRows = relationWithRealEstate.flatMap(rel =>
    (rel.GrundbuchblattArray ?? []).map(b => ({
      id: b.idGrundbuchblatt,
      relationshipId: rel.idRelationship,
      idGrundbuchblatt: b.idGrundbuchblatt,
      amtsgericht: b.Amtsgericht ?? '',
      gemarkung: b.Gemarkung ?? '',
      flurstueckAnzahl: b.FlurstueckAnzahl ?? 0,
      blattnummer: b.Blattnummer ?? '',
    }))
  );

  const handleUnlinkBlatt = (
    relationshipId: number,
    idGrundbuchblatt: number
  ) => {
    const rel = relationsships.find(r => r.idRelationship === relationshipId);
    if (!rel) return;
    updateBlattArray(
      rel,
      (rel.GrundbuchblattArray ?? []).filter(
        b => b.idGrundbuchblatt !== idGrundbuchblatt
      )
    );
  };

  const columns: GridColDef[] = [
    { field: 'amtsgericht', headerName: 'Amtsgericht', flex: 1, minWidth: 140 },
    { field: 'gemarkung', headerName: 'Gemarkung', flex: 1, minWidth: 140 },
    {
      field: 'flurstueckAnzahl',
      headerName: 'Flurstück(e)',
      width: 130,
      type: 'number',
      valueFormatter: params => `${params.value ?? 0} Flurstück(e)`,
    },
    {
      field: 'blattnummer',
      headerName: 'Blatt',
      width: 120,
      renderCell: params => (
        <Chip
          size='small'
          color={params.value ? 'primary' : 'default'}
          variant={params.value ? 'filled' : 'outlined'}
          clickable
          label={params.value || '(Entwurf)'}
          onClick={() => setOpenBlattId(params.row.idGrundbuchblatt)}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Aktionen',
      width: 110,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: params => (
        <Stack direction='row' spacing={0.5}>
          <Tooltip title='Grundbuchblatt öffnen'>
            <IconButton
              size='small'
              color='primary'
              onClick={() => setOpenBlattId(params.row.idGrundbuchblatt)}
            >
              <MenuBook fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Zuordnung zum Vorgang entfernen'>
            <IconButton
              size='small'
              onClick={() =>
                handleUnlinkBlatt(
                  params.row.relationshipId,
                  params.row.idGrundbuchblatt
                )
              }
            >
              <Delete fontSize='small' />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <>
      <AssignRealEstateDialog
        open={isOpenAdd}
        onClose={handleClose}
        onConfirm={handleConfirmRealEstate}
        legalTransaction={legalTransaction}
        onBlaetterAssigned={handleAssignBlaetter}
      />

      {showInlineAddButton && (
        <Tooltip title='Grundstück hinzufügen'>
          <IconButton
            color='primary'
            onClick={onAdd}
            sx={{
              float: 'right',
              border: theme => `1px solid ${theme.palette.primary.main}`,
              borderRadius: 1,
              bgcolor: 'action.hover',
              '&:hover': { bgcolor: 'action.selected' },
            }}
          >
            <Add />
          </IconButton>
        </Tooltip>
      )}
      <Box sx={{ width: '100%' }}>
        <DataGrid
          autoHeight
          rows={blattRows}
          columns={columns}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { page: 0, pageSize: 10 } },
          }}
          slots={{ toolbar: GridToolbar }}
          slotProps={{ toolbar: { showQuickFilter: true } }}
          localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
        />
      </Box>

      {openBlattId != null && (
        <GrundbuchblattEditDialog
          open={openBlattId != null}
          idGrundbuchblatt={openBlattId}
          onClose={() => setOpenBlattId(null)}
          onSaved={() => setOpenBlattId(null)}
        />
      )}
    </>
  );
};
